import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, startWith } from 'rxjs/operators';
import { Project, isMainProject } from 'src/app/classes/project.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { ProjectService } from 'src/app/services/project.service';
import { UserService } from 'src/app/services/user/user.service';
import { Utils } from 'src/app/utils.class';
import { ProjectStatusPipe } from '../project-status.pipe';

export interface ProjectEditDialogData {
    projectId?: string;
    parent_id?: string;
}

export interface ProjectEditDialogResult {
    saved: boolean;
    updatedProject?: Project;
}

@Component({
    selector: 'app-project-edit-dialog',
    templateUrl: './project-edit-dialog.component.html',
    styleUrls: ['./project-edit-dialog.component.scss'],
    standalone: false,
})
export class ProjectEditDialogComponent extends CodaltComponent implements OnInit {
    fgProject: FormGroup<ControlsOf<fgProject>>;
    project: Project;

    executors: User[];
    asphaltExecutors: User[];
    projectManagers: User[];

    saving = false;

    mainProjects: Project[];
    searchAfasProjects$ = new Subject<string>();

    private afasProjectIdManuallyChanged = false;
    private lastAutoGeneratedAfasProjectId: string | null = null;

    groups = [
        { value: 'ALG', label: 'Algemeen' },
        { value: 'OMOP', label: 'Onderhoud meerjarig' },
        { value: 'VV', label: 'Verlof en verzuim' },
    ];

    constructor(
        public dialogRef: MatDialogRef<ProjectEditDialogComponent, ProjectEditDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: ProjectEditDialogData,
        private userService: UserService,
        private projectService: ProjectService
    ) {
        super();
    }

    /**
     * Validator om te controleren of afas_project_id begint met parent_id gevolgd door een -
     */
    private afasProjectIdValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const parentId = this.fgProject?.controls?.parent_id?.value;
            // Een hoofdproject verwijst naar zichzelf; de prefix-eis geldt dan niet.
            if (!parentId || parentId === control.value) {
                return null;
            }

            const expectedPrefix = parentId + '-';
            if (!control.value.startsWith(expectedPrefix)) {
                return {
                    afasProjectIdFormat: {
                        message: `AFAS Project ID moet beginnen met "${expectedPrefix}"`,
                        expectedPrefix: expectedPrefix,
                        actualValue: control.value,
                    },
                };
            }

            return null;
        };
    }

    /**
     * Asynchrone validator om te controleren of afas_project_id al bestaat
     */
    private afasProjectIdExistsValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value || control.value.trim() === '') {
                return of(null);
            }

            const currentProjectAfasId = this.project?.afas_project_id;
            if (currentProjectAfasId && control.value === currentProjectAfasId) {
                return of(null);
            }

            return this.projectService.searchProjects(control.value).pipe(
                map(projects => {
                    const existingProject = projects?.find(p => p.afas_project_id === control.value);
                    if (existingProject) {
                        return {
                            afasProjectIdExists: {
                                message: `AFAS Project ID "${control.value}" bestaat al`,
                                existingProject: existingProject,
                            },
                        };
                    }
                    return null;
                }),
                catchError(() =>
                    // Niet stilzwijgend goedkeuren: anders lijkt een bestaand projectnummer vrij
                    // en overschrijft het opslaan een bestaand project.
                    of({
                        afasProjectIdCheckFailed: {
                            message: 'Kon niet controleren of dit projectnummer al bestaat. Probeer het opnieuw.',
                        },
                    })
                )
            );
        };
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.searchAfasProjects$.pipe(debounceTime(200), startWith(this.data.parent_id)).subscribe(search => {
                this.subscriptions.add(
                    this.projectService.searchProjects(search).subscribe(projects => {
                        this.mainProjects = projects?.filter(isMainProject);
                    })
                );
            })
        );
        this.loadUsers();

        if (this.data?.projectId) {
            this.loadProject(this.data.projectId);
        } else {
            this.initNewProject();
        }
    }

    private loadUsers(): void {
        this.subscriptions.add(
            this.userService.getByType(this.environment.roles.Executor).subscribe(users => {
                this.executors = users;
            })
        );
        this.subscriptions.add(
            this.userService.getByType(this.environment.roles.Asphaltexecutor).subscribe(users => {
                this.asphaltExecutors = users;
            })
        );
        this.subscriptions.add(
            this.userService.getByType(this.environment.roles.Projectmanager).subscribe(users => {
                this.projectManagers = users;
            })
        );
    }

    private initForm(p: Project): void {
        this.fgProject = new FormGroup<ControlsOf<fgProject>>({
            asphalt_performer_id: new FormControl(p.asphalt_performer_id),
            projectmanager_id: new FormControl(p.projectmanager_id),
            performer_id: new FormControl(p.performer_id),
            location: new FormControl(p.location, Validators.maxLength(255)),
            afas_project_id: new FormControl(
                p.afas_project_id,
                // AFAS accepteert maximaal 15 tekens voor PrId.
                [this.afasProjectIdValidator(), Validators.required, Validators.maxLength(15)],
                [this.afasProjectIdExistsValidator()]
            ),
            parent_id: new FormControl(p.parent_id, this.environment.subProjects ? Validators.required : []),
            name: new FormControl(p.name, Validators.required),
            group: new FormControl(p.group, Validators.required),
        });
        if (p.created_at) {
            this.fgProject.controls.afas_project_id.disable();
            this.fgProject.controls.parent_id.disable();
        }
        if (new ProjectStatusPipe().transform(p.status)) {
            this.fgProject.disable();
        }

        if (this.data.parent_id) {
            Utils.triggerValidation(this.fgProject.controls.parent_id);
        }

        // Alleen auto-genereren van afas_project_id voor nieuwe projecten (zonder created_at)
        if (!p.created_at) {
            // Luister naar handmatige wijzigingen van afas_project_id
            this.subscriptions.add(
                this.fgProject.controls.afas_project_id.valueChanges.subscribe(value => {
                    // Als de waarde is gewijzigd door de gebruiker (niet door onze auto-generatie)
                    if (value && value !== this.project.afas_project_id) {
                        this.afasProjectIdManuallyChanged = true;
                    }
                })
            );

            // Voor nieuwe projecten: auto-set groep én auto-genereer afas_project_id
            this.subscriptions.add(
                this.fgProject.controls.parent_id.valueChanges.subscribe(parentId => {
                    if (parentId && !this.fgProject.value.group) {
                        this.fgProject.controls.group.setValue(
                            this.mainProjects.find(p => p.afas_project_id === parentId)?.group
                        );
                    }

                    // Alleen auto-genereren als het veld niet handmatig is gewijzigd
                    if (parentId && !this.afasProjectIdManuallyChanged) {
                        const currentAfasId = this.fgProject.controls.afas_project_id.value;
                        // Vorige auto-waarde hoort bij de oude parent, dus die matcht het
                        // patroon van de nieuwe parent niet; vandaar de vergelijking.
                        const shouldAutoGenerate =
                            !currentAfasId ||
                            currentAfasId === this.lastAutoGeneratedAfasProjectId ||
                            this.isAutoGeneratedPattern(currentAfasId, parentId);

                        if (shouldAutoGenerate) {
                            this.generateNextAfasProjectId(parentId, true);
                        }
                    }
                    this.fgProject.controls.afas_project_id.updateValueAndValidity();
                })
            );
        } else {
            // Voor bestaande projecten alleen validatie updaten, geen wijzigingen aan waarden
            this.subscriptions.add(
                this.fgProject.controls.parent_id.valueChanges.subscribe(parentId => {
                    this.fgProject.controls.afas_project_id.updateValueAndValidity();
                })
            );
        }
    }

    /**
     * Controleert of een afas_project_id een auto-gegenereerd patroon heeft
     * @param afasProjectId Het te controleren afas_project_id
     * @param parentId De parent_id om het patroon mee te vergelijken
     * @returns true als het een auto-gegenereerd patroon lijkt te zijn
     */
    private isAutoGeneratedPattern(afasProjectId: string, parentId: string): boolean {
        if (!afasProjectId || !parentId) {
            return false;
        }

        const expectedPrefix = parentId + '-';
        if (!afasProjectId.startsWith(expectedPrefix)) {
            return false;
        }

        const suffix = afasProjectId.substring(expectedPrefix.length);
        // Check if suffix is a number (optionally zero-padded)
        return /^\d{1,3}$/.test(suffix);
    }

    private initNewProject(): void {
        this.project = new Project();
        if (!this.environment.subProjects) {
            // Geen deelprojecten: direct een hoofdprojectnummer genereren.
            this.generateNextAfasProjectId().then(nextId => {
                this.project.afas_project_id = nextId;
                this.lastAutoGeneratedAfasProjectId = nextId;
                this.initForm(this.project);
            });
        } else if (this.data?.parent_id) {
            this.validateAndSetParentId(this.data.parent_id);
        } else {
            this.initForm(this.project);
        }
    }

    private validateAndSetParentId(parentId: string): void {
        this.subscriptions.add(
            this.projectService.searchProjects(parentId).subscribe({
                next: projects => {
                    const parentProject = projects?.find(p => p.afas_project_id === parentId);

                    if (parentProject) {
                        this.project.parent_id = parentId;
                        this.generateNextAfasProjectId(parentId).then(nextId => {
                            this.project.afas_project_id = nextId;
                            this.lastAutoGeneratedAfasProjectId = nextId;
                            this.initForm(this.project);
                        });
                    } else {
                        this.initForm(this.project);
                    }
                },
                error: error => {
                    this.initForm(this.project);
                },
            })
        );
    }

    /**
     * Berekent het volgende afas_project_id op basis van bestaande projecten
     * @param existingProjects Lijst van bestaande projecten
     * @param parentId Optioneel parent_id voor subprojecten
     * @returns Het volgende afas_project_id
     */
    private calculateNextAfasProjectId(existingProjects: Project[], parentId?: string): string {
        const prefix = parentId ? `${parentId}-` : '';

        if (parentId) {
            // Voor subprojecten: zoek hoogste nummer na parent_id-
            const childProjects = existingProjects.filter(
                p => p.afas_project_id && p.afas_project_id.startsWith(prefix)
            );

            const existingNumbers = childProjects
                .map(p => {
                    const suffix = p.afas_project_id.substring(prefix.length);
                    const num = parseInt(suffix, 10);
                    return isNaN(num) ? 0 : num;
                })
                .filter(num => num > 0);

            const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
            return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
        } else {
            // Voor hoofdprojecten: zoek hoogste nummer
            const mainProjects = existingProjects.filter(p => p.afas_project_id && /^\d+$/.test(p.afas_project_id));

            const existingNumbers = mainProjects.map(p => parseInt(p.afas_project_id, 10)).filter(num => !isNaN(num));

            const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
            return nextNumber.toString().padStart(4, '0');
        }
    }

    /**
     * Genereert een fallback ID bij fouten
     * @param parentId Optioneel parent_id voor subprojecten
     * @returns Fallback afas_project_id
     */
    private getFallbackAfasProjectId(parentId?: string): string {
        return parentId ? `${parentId}-001` : '0001';
    }

    /**
     * Genereert automatisch het volgende valide afas_project_id
     * @param parentId Optioneel parent_id voor subprojecten
     * @param updateFormControl Of het form control moet worden bijgewerkt
     * @returns Promise met het gegenereerde afas_project_id
     */
    private generateNextAfasProjectId(parentId?: string, updateFormControl: boolean = false): Promise<string> {
        return new Promise(resolve => {
            const prefix = parentId ? `${parentId}-` : '';

            this.subscriptions.add(
                this.projectService.searchProjects(prefix || '').subscribe({
                    next: projects => {
                        const existingProjects = projects || [];
                        const nextId = this.calculateNextAfasProjectId(existingProjects, parentId);

                        if (updateFormControl && this.fgProject?.controls?.afas_project_id) {
                            // emitEvent false: anders markeert de listener onze eigen generatie
                            // als handmatige wijziging.
                            this.lastAutoGeneratedAfasProjectId = nextId;
                            this.fgProject.controls.afas_project_id.setValue(nextId, { emitEvent: false });
                            this.fgProject.controls.afas_project_id.updateValueAndValidity({ emitEvent: false });
                        }

                        resolve(nextId);
                    },
                    error: error => {
                        console.warn('Error generating next afas_project_id:', error);
                        const fallbackId = this.getFallbackAfasProjectId(parentId);

                        if (updateFormControl && this.fgProject?.controls?.afas_project_id) {
                            this.lastAutoGeneratedAfasProjectId = fallbackId;
                            this.fgProject.controls.afas_project_id.setValue(fallbackId, { emitEvent: false });
                            this.fgProject.controls.afas_project_id.updateValueAndValidity({ emitEvent: false });
                        }

                        resolve(fallbackId);
                    },
                })
            );
        });
    }

    private loadProject(projectId: string): void {
        this.subscriptions.add(
            this.projectService.getProject(projectId).subscribe(response => {
                this.project = response.data;
                this.initForm(this.project);
            })
        );
    }

    save(): void {
        this.saving = true;
        Utils.triggerValidationP(this.fgProject).then(
            () => {
                Object.assign(this.project, this.fgProject.value);
                this.subscriptions.add(
                    this.projectService.saveProject(this.project, true).subscribe({
                        next: response => {
                            this.saving = false;
                            this.dialogRef.close({
                                saved: true,
                                updatedProject: response?.data || this.project,
                            });
                        },
                        error: error => {
                            this.saving = false;
                            console.error('Error saving project:', error);
                        },
                    })
                );
            },
            () => {
                this.saving = false;
            }
        );
    }

    cancel(): void {
        this.dialogRef.close({ saved: false });
    }
}

export interface fgProject {
    afas_project_id: string;
    parent_id: string;
    name: string;
    group: string;

    projectmanager_id: number;
    performer_id: number;
    asphalt_performer_id: number;
    location: string;
}
